import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { distanceInMeters } from '../utils/geoDistance';
import { useBottleMessages } from './useBottleMessages';
import { useLooting } from './useLooting';
import { useUserGeolocation } from './useUserGeolocation';
import {
  buildResetLocationState,
  buildTrashcanLocationPayload,
  validateNewTrashcanLocation,
} from '../trashcan/domain/payloads';
import {
  normalizeTreasureMapBottleMessages,
  normalizeTreasureMapDataset,
  resolveTreasureMapCategory,
} from '../utils/treasureMapNormalizer';
import { prepareImageUpload } from '../utils/photoUpload';
import { nonce, currentUser, runtime } from '../utils/runtime';
import {
  PICKUP_MAX_ACCURACY_METERS,
  PICKUP_MAX_POSITION_AGE_MS,
  PICKUP_RADIUS_METERS,
} from '../trashcan/config/categoryIcons';
import {
  normalizeLocationRequestBbox,
} from '../game-map/realLocations.mjs';
import { useLocationStore } from '../locations/LocationStoreContext';
import {
  fetchServerOsmSpots,
  mergeLootSpots,
  readCachedServerOsmSpots,
} from '../game-map/lootSpotRepository.mjs';

const LOCATIONS_API_URL = runtime?.locationsApiUrl || '/wp-json/pfandpirat/v1/locations';
const DONATIONS_API_URL =
  '/wp-json/pfandpirat/v1/pfandspenden';
const DONATION_MARK_API_URL = runtime?.appDonationMarkApiUrl || '/wp-json/pfandpirat/v1/pfandspenden/mark';
const DONATION_PICKUP_API_URL =
  runtime?.pfandspendePickupApiUrl || '/wp-json/pfandpirat/v1/pfandspenden-pickup';
const DONATION_PICKUP_COMPAT_API_URL =
  '/wp-json/pfandpirat/v1/pfandspenden/pickup';
const LOCATION_CATEGORIES_API_URL = '/wp-json/pfandpirat/v1/location-categories';

function createInitialState() {
  return {
    locations: false,
    donations: false,
    categories: false,
    addLocation: false,
    pfandAbgeholt: false,
    markDonation: false,
  };
}

function createInitialErrors() {
  return {
    locations: null,
    donations: null,
    categories: null,
    addLocation: null,
    pfandAbgeholt: null,
    markDonation: null,
    geolocation: null,
  };
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function uniqueUrls(urls) {
  return urls.filter((url, index) => url && urls.indexOf(url) === index);
}

function buildDonationPickupUrls(spendeId) {
  const id = encodeURIComponent(String(spendeId || ''));
  return uniqueUrls([
    DONATION_PICKUP_API_URL,
    '/wp-json/pfandpirat/v1/pfandspenden-pickup',
    DONATION_PICKUP_COMPAT_API_URL,
    id ? `/wp-json/pfandpirat/v1/pfandspenden/${id}/pickup` : '',
  ]);
}

function isRestRouteMissing(response, payload) {
  return response?.status === 404 && (!payload || payload.code === 'rest_no_route');
}

async function postDonationPickup(spendeId, requestOptions) {
  let lastResponse = null;
  let lastPayload = null;

  for (const url of buildDonationPickupUrls(spendeId)) {
    const response = await fetch(url, requestOptions);
    const payload = response.ok ? null : await parseJsonSafe(response);

    if (response.ok) {
      return {
        response,
        payload: await parseJsonSafe(response),
        url,
      };
    }

    lastResponse = response;
    lastPayload = payload;

    if (!isRestRouteMissing(response, payload)) {
      break;
    }
  }

  return {
    response: lastResponse,
    payload: lastPayload,
    url: null,
  };
}

function normalizeCategoryEntry(entry) {
  const resolved = resolveTreasureMapCategory(entry?.name ?? entry?.label ?? entry?.slug ?? '', 'sonstiges');

  return {
    id: entry?.id ?? null,
    key: resolved.key,
    name: resolved.label,
    label: resolved.label,
    iconUrl: resolved.iconUrl,
    slug: entry?.slug || resolved.key,
  };
}

export function useTreasureMapData({
  addXP,
  applyGamificationPayload,
  playXpSound,
  pfandNaeheM = PICKUP_RADIUS_METERS,
  viewportBbox = null,
  vectorTilePois = [],
} = {}) {
  const {
    userPos,
    location: geolocationLocation,
    ready,
    defaultPosition,
    accuracy: geolocationAccuracy,
    timestamp: geolocationTimestamp,
    receivedAt: geolocationReceivedAt,
    ageMs: geolocationAgeMs,
    now: geolocationNow,
    status: geolocationStatus,
    error: geolocationError,
    requestFreshPosition,
  } = useUserGeolocation();

  const [rawOsmSpots, setRawOsmSpots] = useState([]);
  const [hybridMeta, setHybridMeta] = useState({ cache: 'idle', partial: false });
  const [rawDonations, setRawDonations] = useState([]);
  const [rawCategories, setRawCategories] = useState([]);
  const [loading, setLoading] = useState(createInitialState);
  const [errors, setErrors] = useState(createInitialErrors);

  const userId = currentUser?.id || Number(window?.pfandpiratData?.user_id || 0) || null;
  const hybridEnabled = Boolean(userId) && runtime?.hybridLootMode === 'all';
  const currentViewportRef = useRef(null);
  const osmRequestRef = useRef(null);
  const donationMarkRef = useRef(null);
  const donationRetryRef = useRef(null);
  const bboxPaddingFactor = Math.max(0, Math.min(0.5, Number(runtime?.gameMapBboxPaddingFactor ?? 0.18)));
  const normalizedViewportBbox = useMemo(
    () => normalizeLocationRequestBbox(viewportBbox, { paddingFactor: bboxPaddingFactor }),
    [viewportBbox, bboxPaddingFactor]
  );
  const {
    locations: rawLocations,
    loading: locationsLoading,
    error: locationsError,
    source: locationSource,
    updatedAt: locationsUpdatedAt,
    cacheAgeMs: locationCacheAgeMs,
    stale: locationsStale,
    veryStale: locationsVeryStale,
    requestCount: locationRequestCount,
    cacheHits: locationCacheHits,
    cacheMisses: locationCacheMisses,
    transferredBytes: locationTransferredBytes,
    lastRequestMs: locationLastRequestMs,
    lastViewport,
    requestViewport: loadLocations,
    refresh: refreshLocations,
  } = useLocationStore();

  const {
    lootMessage,
    canLoot,
    getCooldownTime,
    formatCooldown,
    handleLoot,
    isLooting,
    reloadLootLog,
  } = useLooting({
    userPos,
    userId,
    geolocationAccuracy,
    geolocationTimestamp,
    geolocationReceivedAt,
    requestFreshPosition,
    applyGamificationPayload,
    playXpSound,
  });

  const {
    messages: bottleMessages,
    loading: bottleMessagesLoading,
    busyMessageId,
    openedMessages,
    loadMessages: reloadBottleMessages,
    openMessage,
  } = useBottleMessages({
    applyGamificationPayload,
  });

  useEffect(() => {
    setErrors((previous) => ({
      ...previous,
      geolocation: geolocationError || null,
    }));
  }, [geolocationError]);

  const loadCategories = useCallback(async () => {
    setLoading((previous) => ({
      ...previous,
      categories: true,
    }));

    setErrors((previous) => ({
      ...previous,
      categories: null,
    }));

    try {
      const response = await fetch(LOCATION_CATEGORIES_API_URL, {
        credentials: 'include',
        headers: nonce ? { 'X-WP-Nonce': nonce } : undefined,
      });

      if (!response.ok) {
        throw new Error(`Kategorien-API antwortete mit HTTP ${response.status}.`);
      }

      const payload = await parseJsonSafe(response);
      setRawCategories(Array.isArray(payload) ? payload.map(normalizeCategoryEntry) : []);
    } catch (error) {
      setRawCategories([]);
      setErrors((previous) => ({
        ...previous,
        categories: error?.message || 'Kategorien konnten nicht geladen werden.',
      }));
    } finally {
      setLoading((previous) => ({
        ...previous,
        categories: false,
      }));
    }
  }, []);

  const loadDonations = useCallback(async () => {
    setLoading((previous) => ({
      ...previous,
      donations: true,
    }));

    setErrors((previous) => ({
      ...previous,
      donations: null,
    }));

    try {
      const response = await fetch(DONATIONS_API_URL, {
        credentials: 'include',
        headers: nonce ? { 'X-WP-Nonce': nonce } : undefined,
      });

      if (!response.ok) {
        throw new Error(`Pfandspenden-API antwortete mit HTTP ${response.status}.`);
      }

      const payload = await parseJsonSafe(response);
      setRawDonations(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setRawDonations([]);
      setErrors((previous) => ({
        ...previous,
        donations: error?.message || 'Pfandspenden konnten nicht geladen werden.',
      }));
    } finally {
      setLoading((previous) => ({
        ...previous,
        donations: false,
      }));
    }
  }, []);

  const loadOsmSpots = useCallback(async (requestedViewportBbox = null) => {
    if (!hybridEnabled) {
      setRawOsmSpots([]);
      setHybridMeta({ cache: 'disabled', partial: false });
      return;
    }
    const normalizedBbox = requestedViewportBbox?.key ? requestedViewportBbox : normalizeLocationRequestBbox(requestedViewportBbox, { paddingFactor: bboxPaddingFactor });
    if (!normalizedBbox) return;
    osmRequestRef.current?.abort();
    const controller = new AbortController();
    osmRequestRef.current = controller;
    try {
      const cached = await readCachedServerOsmSpots({ bbox: normalizedBbox });
      if (osmRequestRef.current !== controller) return;
      if (cached?.spots?.length) {
        setRawOsmSpots(cached.spots);
        setHybridMeta(cached.meta || { cache: 'indexeddb', partial: false });
      }
      const result = await fetchServerOsmSpots({ bbox: normalizedBbox, nonce, enabled: hybridEnabled, signal: controller.signal });
      if (osmRequestRef.current !== controller) return;
      setRawOsmSpots(Array.isArray(result.spots) ? result.spots : []);
      setHybridMeta(result.meta || { cache: 'network', partial: false });
    } catch (error) {
      if (error?.name !== 'AbortError') setHybridMeta({ cache: 'error', partial: true });
    }
  }, [bboxPaddingFactor, hybridEnabled]);

  useEffect(() => () => {
    osmRequestRef.current?.abort();
  }, []);

  useEffect(() => {
    loadDonations();
    loadCategories();
  }, [loadDonations, loadCategories]);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') void loadDonations(); };
    const interval = window.setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadDonations]);

  useEffect(() => {
    if (!normalizedViewportBbox) {
      return;
    }

    const timer = window.setTimeout(() => {
      currentViewportRef.current = normalizedViewportBbox;
      loadLocations(normalizedViewportBbox);
      if (hybridEnabled) loadOsmSpots(normalizedViewportBbox);
    }, 80);

    return () => {
      window.clearTimeout(timer);
    };
  }, [hybridEnabled, loadLocations, loadOsmSpots, normalizedViewportBbox]);

  const normalizedDataset = useMemo(() => {
    return normalizeTreasureMapDataset({
      locations: rawLocations,
      donations: rawDonations,
      userPos,
      getCooldownTime,
    });
  }, [rawLocations, rawDonations, userPos, getCooldownTime]);

  const hybridLocations = useMemo(() => mergeLootSpots({
    wordpress: normalizedDataset.locations,
    vector: hybridEnabled ? vectorTilePois : [],
    osm: hybridEnabled ? rawOsmSpots : [],
  }), [hybridEnabled, normalizedDataset.locations, rawOsmSpots, vectorTilePois]);

  const hybridTreasureMapItems = useMemo(() => {
    const donations = Array.isArray(normalizedDataset.donations) ? normalizedDataset.donations : [];
    return [...hybridLocations, ...donations].sort((left, right) => {
      const leftDistance = Number.isFinite(left?.distanceMeters) ? left.distanceMeters : Number.MAX_SAFE_INTEGER;
      const rightDistance = Number.isFinite(right?.distanceMeters) ? right.distanceMeters : Number.MAX_SAFE_INTEGER;
      return leftDistance - rightDistance;
    });
  }, [hybridLocations, normalizedDataset.donations]);

  const normalizedBottleMessages = useMemo(() => {
    return normalizeTreasureMapBottleMessages(bottleMessages, {
      userPos,
      openedMessages,
    });
  }, [bottleMessages, userPos, openedMessages]);

  const mergedCategories = useMemo(() => {
    const categoryMap = new Map();

    (Array.isArray(rawCategories) ? rawCategories : []).forEach((category) => {
      if (category?.key) {
        categoryMap.set(category.key, category);
      }
    });

    (Array.isArray(normalizedDataset.categories) ? normalizedDataset.categories : []).forEach((category) => {
      if (category?.key && !categoryMap.has(category.key)) {
        categoryMap.set(category.key, category);
      }
    });

    return Array.from(categoryMap.values()).sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || ''), 'de'));
  }, [rawCategories, normalizedDataset.categories]);

  const handlePfandAbgeholt = useCallback(
    async (spende, evidence = {}) => {
      setErrors((previous) => ({
        ...previous,
        pfandAbgeholt: null,
      }));

      setLoading((previous) => ({
        ...previous,
        pfandAbgeholt: true,
      }));

      try {
        if (!nonce) {
          throw new Error('Sicherheits-Token (Nonce) fehlt.');
        }

        if (geolocationStatus === 'denied') {
          throw new Error('Standortfreigabe verweigert. Ohne aktuellen Standort kann die Abholung nicht bestaetigt werden.');
        }

        let activePosition = geolocationLocation;
        let positionAgeMs = activePosition?.receivedAt ? Date.now() - Number(activePosition.receivedAt) : Number.POSITIVE_INFINITY;
        if (!Number.isFinite(positionAgeMs) || positionAgeMs > PICKUP_MAX_POSITION_AGE_MS) {
          if (typeof requestFreshPosition !== 'function') {
            throw new Error('Deine aktuelle Position ist unbekannt. Bitte Standort freigeben.');
          }
          activePosition = await requestFreshPosition();
          positionAgeMs = activePosition?.receivedAt ? Date.now() - Number(activePosition.receivedAt) : Number.POSITIVE_INFINITY;
          if (!Number.isFinite(positionAgeMs) || positionAgeMs > PICKUP_MAX_POSITION_AGE_MS) {
            throw new Error('Der GPS-Standort konnte nicht rechtzeitig aktualisiert werden. Bitte versuche es erneut.');
          }
        }

        if (!activePosition || !Number.isFinite(Number(activePosition.latitude)) || !Number.isFinite(Number(activePosition.longitude))) {
          throw new Error('Deine aktuelle Position ist unbekannt. Bitte Standort freigeben.');
        }

        if (!Number.isFinite(Number(activePosition.accuracy)) || Number(activePosition.accuracy) > PICKUP_MAX_ACCURACY_METERS) {
          throw new Error('Standort ist noch zu ungenau. Warte kurz auf ein besseres GPS-Signal.');
        }

        const lat = Number(spende?.lat ?? spende?.latitude);
        const lng = Number(spende?.lng ?? spende?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          throw new Error('Koordinaten der Pfandspende fehlen oder sind ungültig.');
        }

        const distance = distanceInMeters(
          { latitude: lat, longitude: lng },
          { latitude: activePosition.latitude, longitude: activePosition.longitude }
        );

        if (distance > pfandNaeheM) {
          throw new Error(`Du bist noch ${Math.round(distance)} m entfernt. Geh näher ran.`);
        }

        const cleanBarcode = String(evidence?.barcode || '').replace(/\D/g, '').slice(0, 14);
        const hasPhoto = Boolean(evidence?.photo);
        const hasEvidence = hasPhoto || cleanBarcode;
        let requestOptions;

        if (hasEvidence) {
          const formData = new FormData();
          formData.append('id', spende.id);
          formData.append('latitude', activePosition.latitude);
          formData.append('longitude', activePosition.longitude);
          formData.append('position_timestamp', Number(activePosition.receivedAt || activePosition.timestamp));
          formData.append('position_source', 'gps');
          if (Number.isFinite(Number(activePosition.accuracy))) {
            formData.append('accuracy', Number(activePosition.accuracy));
          }
          if (cleanBarcode) {
            formData.append('barcode', cleanBarcode);
          }
          if (hasPhoto) {
            formData.append('photo', await prepareImageUpload(evidence.photo, { outputBaseName: 'pfandpirat-abholung' }));
          }

          requestOptions = {
            method: 'POST',
            credentials: 'include',
            headers: {
              'X-WP-Nonce': nonce,
            },
            body: formData,
          };
        } else {
          requestOptions = {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-WP-Nonce': nonce,
            },
            body: JSON.stringify({
              id: spende.id,
              latitude: activePosition.latitude,
              longitude: activePosition.longitude,
              accuracy: Number(activePosition.accuracy),
              position_timestamp: Number(activePosition.receivedAt || activePosition.timestamp),
              position_source: 'gps',
            }),
          };
        }

        const pickupResult = await postDonationPickup(spende.id, requestOptions);
        const pickupRes = pickupResult.response;
        const errorPayload = pickupResult.payload;

        if (!pickupRes || !pickupRes.ok) {
          throw new Error(errorPayload?.message || `HTTP ${pickupRes?.status || 0}`);
        }

        const pickupPayloadResponse = pickupResult.payload;
        const removedId = pickupPayloadResponse?.id || spende.id;
        const collectorXp = pickupPayloadResponse?.collector_xp || pickupPayloadResponse?.rewards?.collector_xp || null;

        if (collectorXp && Number(collectorXp.awarded || 0) > 0 && typeof applyGamificationPayload === 'function') {
          applyGamificationPayload(collectorXp, {
            emitXpToast: true,
            event: collectorXp.event || 'pickup_pfandspende',
            awarded: Number(collectorXp.awarded || 0),
            toastLabel: collectorXp?.xp_toast?.label || 'Pfandspende abgeholt',
          });
        }

        setRawDonations((previous) => previous.filter((entry) => String(entry.id) !== String(removedId) && String(entry.id) !== String(spende.id)));
        return { ok: true, id: removedId };
      } catch (error) {
        let message = error?.message || 'Pfandspende konnte nicht aktualisiert werden.';
        if (message.includes('keine Route gefunden') || message.includes('rest_no_route')) {
          message = 'Der Pickup-Endpunkt ist im Backend noch nicht aktiv. Bitte Plugin-Dateien vollständig hochladen und Cache leeren.';
        }
        setErrors((previous) => ({
          ...previous,
          pfandAbgeholt: message,
        }));
        alert(message);
        return { ok: false, message };
      } finally {
        setLoading((previous) => ({
          ...previous,
          pfandAbgeholt: false,
        }));
      }
    },
    [applyGamificationPayload, geolocationLocation, geolocationStatus, pfandNaeheM, requestFreshPosition]
  );

  const markDonation = useCallback(async (counts) => {
    if (donationMarkRef.current) return donationMarkRef.current;
    const request = (async () => {
      setLoading((previous) => ({ ...previous, markDonation: true }));
      setErrors((previous) => ({ ...previous, markDonation: null }));
      try {
        if (!nonce || !userId) throw new Error('Bitte melde dich an, um Beute zu markieren.');
        let position = geolocationLocation;
        const ageMs = position?.receivedAt ? Date.now() - Number(position.receivedAt) : Number.POSITIVE_INFINITY;
        if (!position || ageMs > 15000) position = await requestFreshPosition?.();
        if (!position) throw new Error('Es konnte innerhalb von zehn Sekunden kein frischer GPS-Standort ermittelt werden.');
        if (Number(position.accuracy) > PICKUP_MAX_ACCURACY_METERS) throw new Error('Standort ist noch zu ungenau. Warte kurz auf ein besseres GPS-Signal.');
        const countSignature = JSON.stringify([
          Number(counts?.glass_count || 0),
          Number(counts?.pet_reusable_count || 0),
          Number(counts?.one_way_count || 0),
        ]);
        if (donationRetryRef.current?.signature !== countSignature) {
          donationRetryRef.current = {
            signature: countSignature,
            requestId: globalThis.crypto?.randomUUID?.() || `donation-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          };
        }
        const requestId = donationRetryRef.current.requestId;
        const response = await fetch(DONATION_MARK_API_URL, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
          body: JSON.stringify({
            glass_count: Number(counts?.glass_count || 0),
            pet_reusable_count: Number(counts?.pet_reusable_count || 0),
            one_way_count: Number(counts?.one_way_count || 0),
            latitude: Number(position.latitude), longitude: Number(position.longitude),
            accuracy: Number(position.accuracy), position_timestamp: Number(position.receivedAt || Date.now()),
            position_source: 'gps', request_id: requestId,
          }),
        });
        const payload = await parseJsonSafe(response);
        if (!response.ok || !payload?.success || !payload?.donation) throw new Error(payload?.message || `Pfandspenden-API antwortete mit HTTP ${response.status}.`);
        donationRetryRef.current = null;
        setRawDonations((previous) => [payload.donation, ...previous.filter((entry) => String(entry?.id) !== String(payload.donation.id))]);
        if (payload?.xp && typeof applyGamificationPayload === 'function') applyGamificationPayload(payload.xp, { emitXpToast: true, event: 'mark_pfandspende', toastLabel: 'Pfandspende markiert' });
        return payload.donation;
      } catch (error) {
        setErrors((previous) => ({ ...previous, markDonation: error?.message || 'Die Pfandspende konnte nicht markiert werden.' }));
        throw error;
      } finally {
        setLoading((previous) => ({ ...previous, markDonation: false }));
      }
    })();
    donationMarkRef.current = request;
    try { return await request; } finally { donationMarkRef.current = null; }
  }, [applyGamificationPayload, geolocationLocation, requestFreshPosition, userId]);

  const addLocation = useCallback(
    async (draftLocation) => {
      setErrors((previous) => ({
        ...previous,
        addLocation: null,
      }));

      setLoading((previous) => ({
        ...previous,
        addLocation: true,
      }));

      try {
        const validation = validateNewTrashcanLocation(draftLocation);
        if (!validation.ok) {
          throw new Error(validation.message || 'Bitte alle Pflichtfelder ausfüllen.');
        }

        if (!nonce) {
          throw new Error('Sicherheits-Token (Nonce) fehlt.');
        }

        const response = await fetch(LOCATIONS_API_URL, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
          },
          body: JSON.stringify(buildTrashcanLocationPayload(draftLocation)),
        });

        if (!response.ok) {
          const errorPayload = await parseJsonSafe(response);
          throw new Error(
            errorPayload?.message || 'Fehler beim Hinzufügen. Du hast eventuell keine Berechtigung.'
          );
        }

        await refreshLocations(currentViewportRef.current);

        return {
          ok: true,
          resetState: buildResetLocationState(draftLocation),
        };
      } catch (error) {
        const message = error?.message || 'Ort konnte nicht hinzugefügt werden.';
        setErrors((previous) => ({
          ...previous,
          addLocation: message,
        }));
        alert(message);
        return {
          ok: false,
          message,
        };
      } finally {
        setLoading((previous) => ({
          ...previous,
          addLocation: false,
        }));
      }
    },
    [refreshLocations]
  );

  const reloadAll = useCallback(async () => {
    await Promise.all([
      refreshLocations(currentViewportRef.current),
      loadDonations(),
      loadCategories(),
      reloadLootLog(),
      reloadBottleMessages(),
    ]);
  }, [refreshLocations, loadDonations, loadCategories, reloadLootLog, reloadBottleMessages]);

  return {
    userId,
    ready,
    userPos,
    geolocationLocation,
    geolocationAccuracy,
    geolocationStatus,
    geolocationTimestamp,
    geolocationReceivedAt,
    geolocationAgeMs,
    geolocationNow,
    defaultPosition,
    loading: { ...loading, locations: locationsLoading },
    errors: { ...errors, locations: locationsError },
    rawLocations,
    rawDonations,
    rawOsmSpots,
    hybridMeta,
    allLocations: hybridLocations,
    locationDiagnostics: normalizedDataset.locationDiagnostics,
    locationCacheState: {
      source: locationSource,
      updatedAt: locationsUpdatedAt,
      ageMs: locationCacheAgeMs,
      stale: locationsStale,
      veryStale: locationsVeryStale,
      requestCount: locationRequestCount,
      cacheHits: locationCacheHits,
      cacheMisses: locationCacheMisses,
      transferredBytes: locationTransferredBytes,
      lastRequestMs: locationLastRequestMs,
    },
    lastViewport,
    pfandSpenden: normalizedDataset.donations,
    markDonation,
    treasureMapItems: hybridTreasureMapItems,
    categories: mergedCategories,
    treasureMapBottleMessages: normalizedBottleMessages,
    bottleMessages: normalizedBottleMessages,
    bottleMessagesLoading,
    busyBottleMessageId: busyMessageId,
    openedBottleMessages: openedMessages,
    openBottleMessage: openMessage,
    lootMessage,
    canLoot,
    getCooldownTime,
    formatCooldown,
    handleLoot,
    isLooting,
    handlePfandAbgeholt,
    addLocation,
    reloadAll,
    reloadLocations: (options) => refreshLocations(options ?? currentViewportRef.current),
    reloadLootLog,
    reloadBottleMessages,
  };
}

export default useTreasureMapData;
